import { useAuthStore } from '@/shared/stores/authStore';
import {
  Users,
  DollarSign,
  TrendingUp,
  Package,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Eye
} from 'lucide-react';
import AdminHeader from '@/react-app/components/AdminHeader';
import { useAdminStats } from "./hooks/useAdminStats";
import { useNavigate } from "react-router-dom";


export default function AdminDashboard() {
  const { user: authStoreUser, isAuthenticated } = useAuthStore();

  const { data: stats, isLoading } = useAdminStats("", 0, "");

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const navigate = useNavigate();


  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'withdrawal':
        return <ArrowUpRight className="h-4 w-4 text-red-500" />;
      case 'user_registration':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'return_payment':
        return <TrendingUp className="h-4 w-4 text-purple-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
    };

    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || authStoreUser?.role == "user") {
    window.location.href = '/login';
    return null;
  }

  const redirectUsers = () => {
    navigate('/admin/users')
  }

  const redirectPackage = () => {
    navigate('/admin/packages')
  }

  const redirectAnalytics = () => {
    navigate('/admin/analytics')
  }

  const riding = () => {
    navigate('/admin/transactions')
  }

  return (
    <div className="min-h-screen dark:bg-gray-900 bg-gray-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Manage and monitor your Trade Enrich platform
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {stats?.totalUsers?.toLocaleString() || '0'}
            </p>
            <div className="flex items-center mt-2">
              <span className="text-sm text-gray-500">
                {stats?.totalMonthActiveUsers.toLocaleString()} active this month
              </span>
            </div>
          </div>

          {/* Total Deposits */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Deposits</h3>
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(stats?.globalTotals.totalDeposits || 0)}
            </p>
            <div className="flex items-center mt-2">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600">
                +{stats?.packageTotals[0]?.currentMonthDeposits?.toFixed(1) || '0'}% this month
              </span>
            </div>
          </div>

          {/* Total Balance */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Balance</h3>
              <Package className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(stats?.globalTotals.totalBalance || 0)}
            </p>
            <div className="flex items-center mt-2">
              <span className="text-sm text-gray-500">
                Platform TVL
              </span>
            </div>
          </div>

          {/* Pending Deposits */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Payouts</h3>
              <DollarSign className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(stats?.globalTotals.totalWithdrawals || 0)}
            </p>
            {/* <div className="flex items-center mt-2">
              <Clock className="h-4 w-4 text-orange-500 mr-1" />
              <span className="text-sm text-orange-600">Requires attention</span>
            </div> */}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                <button onClick={riding} className="text-red-600 text-sm font-medium hover:text-red-700 transition-colors">
                  View All
                </button>
              </div>

              {stats?.recentTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-gray-900 font-medium mb-2">No recent activity</h4>
                  <p className="text-gray-500 text-sm">
                    Activity will appear here as users interact with the platform
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {stats?.recentTransactions.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{activity.description}</p>
                          <div className="flex items-center space-x-2">
                            <p className="text-sm text-gray-500">{activity.user_email}</p>
                            <span className="text-gray-300">•</span>
                            <p className="text-sm text-gray-500">
                              {new Date(activity.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {activity.amount && (
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(activity.amount)}
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(activity.status)}`}>
                          {activity.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions & Alerts */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button onClick={redirectUsers} className="w-full bg-red-600 text-white p-3 rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Manage Users</span>
                </button>
                <button onClick={redirectPackage} className="w-full bg-gray-100 text-gray-700 p-3 rounded-xl font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2">
                  <Package className="h-5 w-5" />
                  <span>Add Package</span>
                </button>
                <button onClick={redirectAnalytics} className="w-full bg-gray-100 text-gray-700 p-3 rounded-xl font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2">
                  <Eye className="h-5 w-5" />
                  <span>View Analytics</span>
                </button>
              </div>
            </div>

            {/* System Alerts */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-800 text-sm font-medium">Database</span>
                  </div>
                  <span className="text-green-600 text-xs font-medium">Healthy</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-800 text-sm font-medium">API Services</span>
                  </div>
                  <span className="text-green-600 text-xs font-medium">Online</span>
                </div>
                {/* <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    <span className="text-yellow-800 text-sm font-medium">Pending Actions</span>
                  </div>
                  <span className="text-yellow-600 text-xs font-medium">{stats?.pending_deposits || 0}</span>
                </div> */}
              </div>
            </div>

            {/* Platform Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Earnings Paid</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(stats?.globalTotals.totalWithdrawals || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Referral Bonuses</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(stats?.globalTotals.totalReferralEarnings || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform Uptime</span>
                  <span className="font-semibold text-green-600">99.9%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
